/**
 * Utilitário central para acesso a storage (session/local) com TTL e validação opcional.
 * Objetivo: padronizar leitura/escrita, habilitar expiração e facilitar migrações futuras.
 * 
 * Recursos v2:
 * - smartGet: leitura com fallback para chaves legadas (migração automática)
 * - dualWrite: escrita dupla (nova + legadas) para transição segura
 * - migrateKey: migração explícita de chave legada
 */

import { sendToTerminal, terminalLog } from '../terminalLogger';

export type StorageArea = 'session' | 'local';

export interface StorageMeta {
  createdAt: number;
  expiresAt?: number;
  version?: number;
  /** ETag retornado pelo servidor para validação de cache */
  etag?: string;
}

export interface GetOptions<T = unknown> {
  area?: StorageArea;
  validate?: (value: unknown) => value is T;
  fallback?: T;
}

export interface SetOptions {
  area?: StorageArea;
  ttlMs?: number;
  version?: number;
  /** ETag do servidor para validação de cache HTTP */
  etag?: string;
}

interface Envelope<T = unknown> {
  _meta: StorageMeta;
  data: T;
}

function getStore(area: StorageArea): Storage {
  return area === 'local' ? window.localStorage : window.sessionStorage;
}

function now(): number {
  return Date.now();
}

function isExpired(meta?: StorageMeta): boolean {
  if (!meta || typeof meta.expiresAt !== 'number') return false;
  return now() > meta.expiresAt;
}

function safeParse(raw: string | null): unknown | null {
  if (raw == null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function toEnvelope<T>(value: T, opts?: SetOptions): Envelope<T> {
  const meta: StorageMeta = {
    createdAt: now(),
    version: typeof opts?.version === 'number' ? opts?.version : undefined,
    expiresAt: typeof opts?.ttlMs === 'number' && opts.ttlMs > 0 ? now() + opts.ttlMs : undefined,
    etag: opts?.etag,
  };
  return { _meta: meta, data: value };
}

function fromEnvelope<T>(parsed: unknown): { data: T | null; meta?: StorageMeta } {
  // Envelope esperado: { _meta: { ... }, data: <valor> }
  if (
    parsed != null &&
    typeof parsed === 'object' &&
    '_meta' in (parsed as any) &&
    'data' in (parsed as any)
  ) {
    const env = parsed as Envelope<T>;
    return { data: env.data, meta: env._meta };
  }
  // Backward-compat: armazenado como raw JSON (sem envelope)
  return { data: parsed as T };
}

export const storage = {
  /**
   * Obtém valor do storage (versão básica)
   */
  get<T = unknown>(key: string, opts?: GetOptions<T>): T | null {
    const area: StorageArea = opts?.area ?? 'session';
    const store = getStore(area);
    const raw = store.getItem(key);
    const parsed = safeParse(raw);
    const { data, meta } = fromEnvelope<T>(parsed);

    // Expiração: remover e retornar fallback/null
    if (isExpired(meta)) {
      try { store.removeItem(key); } catch {}
      return opts?.fallback ?? null;
    }

    // Validação opcional
    if (opts?.validate && !opts.validate(data as unknown)) {
      return opts.fallback ?? null;
    }

    // Quando ausente
    if (data == null) return opts?.fallback ?? null;
    return data;
  },

  /**
   * Obtém valor do storage COM metadados (incluindo ETag)
   * Útil para cache com validação HTTP ETag
   *
   * @returns { data, meta } onde meta contém etag, createdAt, etc.
   */
  getWithMeta<T = unknown>(key: string, opts?: GetOptions<T>): { data: T | null; meta: StorageMeta | null } {
    const area: StorageArea = opts?.area ?? 'session';
    const store = getStore(area);
    const raw = store.getItem(key);
    const parsed = safeParse(raw);
    const { data, meta } = fromEnvelope<T>(parsed);

    // Expiração: remover e retornar null
    if (isExpired(meta)) {
      try { store.removeItem(key); } catch {}
      return { data: null, meta: null };
    }

    // Validação opcional
    if (opts?.validate && !opts.validate(data as unknown)) {
      return { data: null, meta: null };
    }

    // Quando ausente
    if (data == null) return { data: null, meta: null };
    return { data, meta: meta ?? null };
  },

  /**
   * Define valor no storage (versão básica)
   */
  set<T = unknown>(key: string, value: T, opts?: SetOptions): void {
    const area: StorageArea = opts?.area ?? 'session';
    const store = getStore(area);
    const envelope = toEnvelope<T>(value, opts);
    try {
      store.setItem(key, JSON.stringify(envelope));
    } catch (error) {
      // Storage cheio ou indisponível
      try {
        terminalLog.error('[STORAGE] Erro ao gravar no storage', {
          key,
          area,
          error: error instanceof Error ? error.message : String(error),
          errorName: error instanceof Error ? error.name : 'Unknown',
          note: 'Possível storage cheio ou indisponível'
        });
      } catch {}
    }
  },

  /**
   * Remove valor do storage
   */
  remove(key: string, area: StorageArea = 'session'): void {
    const store = getStore(area);
    try { store.removeItem(key); } catch {}
  },

  /**
   * smartGet: leitura com fallback para chaves legadas
   * Tenta ler da chave primária, se não encontrar tenta chaves legadas (em ordem)
   * e migra automaticamente para a chave primária
   * 
   * @param primaryKey - Chave principal (novo padrão)
   * @param legacyKeys - Array de chaves legadas para fallback
   * @param opts - Opções de leitura
   * @returns Valor encontrado ou null
   */
  smartGet<T = unknown>(
    primaryKey: string,
    legacyKeys: string[],
    opts?: GetOptions<T>
  ): T | null {
    const area: StorageArea = opts?.area ?? 'session';
    
    // 1. Tentar chave primária primeiro
    const primaryValue = this.get<T>(primaryKey, { ...opts, area });
    if (primaryValue !== null) {
      return primaryValue;
    }
    
    // 2. Tentar chaves legadas (em ordem)
    for (const legacyKey of legacyKeys) {
      const legacyValue = this.get<T>(legacyKey, { ...opts, area });
      
      if (legacyValue !== null) {
        // Migração automática
        try {
          sendToTerminal('🔄 [STORAGE:MIGRATION]', {
            from: legacyKey,
            to: primaryKey,
            area,
            timestamp: new Date().toISOString(),
          });
        } catch {}
        
        // Copiar para chave primária (com mesmas opções)
        this.set(primaryKey, legacyValue, { area, ...opts });
        
        // Remover chave legada após migração
        this.remove(legacyKey, area);
        
        return legacyValue;
      }
    }
    
    // 3. Não encontrado
    return opts?.fallback ?? null;
  },

  /**
   * dualWrite: escrita dupla em chave primária + chaves legadas
   * Garante compatibilidade durante período de transição
   * 
   * @param primaryKey - Chave principal (novo padrão)
   * @param legacyKeys - Array de chaves legadas para escrever também
   * @param value - Valor a ser salvo
   * @param opts - Opções de escrita
   */
  dualWrite<T = unknown>(
    primaryKey: string,
    legacyKeys: string[],
    value: T,
    opts?: SetOptions & { enableDualWrite?: boolean }
  ): void {
    const area: StorageArea = opts?.area ?? 'session';
    const enableDualWrite = opts?.enableDualWrite ?? true;
    
    terminalLog.debug('[STORAGE:DUALWRITE] Iniciando', {
      primaryKey,
      legacyKeys,
      area,
      enableDualWrite,
      hasValue: value != null,
      valueType: typeof value,
      hasCaminhoArquivo: !!(value as any)?.caminho_arquivo
    });

    // Sempre escreve na chave primária
    this.set(primaryKey, value, { ...opts, area });

    // Escreve nas chaves legadas se habilitado
    if (enableDualWrite) {
      legacyKeys.forEach(legacyKey => {
        this.set(legacyKey, value, { ...opts, area });
      });
    }
  },

  /**
   * migrateKey: migração explícita de uma chave legada para nova
   * Remove a chave legada após migração bem-sucedida
   * 
   * @param oldKey - Chave legada
   * @param newKey - Nova chave
   * @param area - Área do storage
   * @returns true se migrou, false se não havia dados na chave legada
   */
  migrateKey<T = unknown>(
    oldKey: string,
    newKey: string,
    area: StorageArea = 'session'
  ): boolean {
    const oldValue = this.get<T>(oldKey, { area });
    
    if (oldValue === null) {
      return false; // Nada para migrar
    }
    
    // Copiar para nova chave
    this.set(newKey, oldValue, { area });
    
    // Remover chave antiga
    this.remove(oldKey, area);
    
    try {
      sendToTerminal('✅ [STORAGE:MIGRATE_KEY]', {
        from: oldKey,
        to: newKey,
        area,
        timestamp: new Date().toISOString(),
      });
    } catch {}
    
    return true;
  },

  /**
   * Limpa todas as chaves (primária + legadas)
   */
  clearAll(primaryKey: string, legacyKeys: string[], area: StorageArea = 'session'): void {
    this.remove(primaryKey, area);
    legacyKeys.forEach(key => this.remove(key, area));
  },

  now,
  isExpired,
};

export default storage;


